import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Logo.css';

/**
 * Pure SVG logo — pixel-perfect recreation of The Unexplored Routes logo.
 *
 * Structure:
 * - U-shaped ROAD drawn as a thick stroke path (not fill)
 * - White dashed center line runs along the CENTER of the road
 * - Left leg has flat cap at top
 * - Right leg ends in upward arrow head
 * - Golden animated car travels the U path on load + hover
 */
export default function AnimatedLogo({ size = 48, showText = true, variant = 'auto' }) {
  const [animKey, setAnimKey] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setAnimKey(1), 500);
    return () => clearTimeout(t);
  }, []);

  const handleHover = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setAnimKey(k => k + 1), 60);
  };

  // ViewBox: 0 0 100 100
  // Road center path — this is the skeleton of the U road
  // Left leg top → down → curve → right leg up → arrow tip
  // Road is drawn as thick stroke on this path
  const ROAD_CENTER = "M 28,5 L 28,55 Q 28,88 50,88 Q 72,88 72,55 L 72,5";

  // Car follows the same center path + continues past arrow
  const CAR_PATH = "M 28,8 L 28,55 Q 28,88 50,88 Q 72,88 72,55 L 72,5 L 72,-15";

  const ROAD_WIDTH = 18;   // thickness of the road stroke
  const DASH_WIDTH = 2.5;  // dashed center line stroke width

  return (
    <Link to="/" className="logo-link" onMouseEnter={handleHover} aria-label="The Unexplored Routes">
      <div className="logo-wrap">

        {/* SVG mark */}
        <svg
          className="logo-svg-mark"
          width={size}
          height={Math.round(size * 1.05)}
          viewBox="0 0 100 105"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          overflow="visible"
        >
          {/* ══════════════════════════════════════════
              1. ROAD — thick stroke along U center path
                 This creates the road body (black/dark)
              ══════════════════════════════════════════ */}
          <path
            d={ROAD_CENTER}
            stroke="currentColor"
            strokeWidth={ROAD_WIDTH}
            strokeLinecap="butt"
            strokeLinejoin="round"
            fill="none"
            className="logo-road-stroke"
          />

          {/* ══════════════════════════════════════════
              2. FLAT TOP CAP on left leg (the horizontal bar)
              ══════════════════════════════════════════ */}
          <line
            x1={28 - ROAD_WIDTH / 2}
            y1="5"
            x2={28 + ROAD_WIDTH / 2}
            y2="5"
            stroke="currentColor"
            strokeWidth={ROAD_WIDTH * 0.55}
            strokeLinecap="square"
            className="logo-road-stroke"
          />

          {/* ══════════════════════════════════════════
              3. ARROW HEAD on right leg (upward pointing)
              ══════════════════════════════════════════ */}
          <polygon
            points={`${72 - ROAD_WIDTH * 0.95},5 ${72 + ROAD_WIDTH * 0.95},5 72,-14`}
            fill="currentColor"
            className="logo-road-stroke"
          />

          {/* ══════════════════════════════════════════
              4. DASHED CENTER LINE
                 Road=dark  → dash=white (default)
                 Road=white → dash=dark background color
                 Controlled via CSS class on the path
              ══════════════════════════════════════════ */}
          <path
            d={ROAD_CENTER}
            strokeWidth={DASH_WIDTH}
            strokeDasharray="5.5,4"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            className="logo-dash"
          />

          {/* ══════════════════════════════════════════
              5. ANIMATED CAR — travels U path on load/hover
              ══════════════════════════════════════════ */}
          {animKey > 0 && (
            <g key={`car-${animKey}`} className="logo-car-group">
              {/* Car body */}
              <rect x="-5.5" y="-3"   width="11" height="6.5" rx="2"   fill="#d4a017" />
              {/* Cabin roof */}
              <rect x="-3.2" y="-6.5" width="6.4" height="4"  rx="1.5" fill="#b8860b" />
              {/* Windshield */}
              <rect x="-2.5" y="-6"   width="5"   height="3"  rx="1"   fill="rgba(255,255,255,0.75)" />
              {/* Headlights */}
              <ellipse cx=" 5.2" cy="0.8" rx="1.1" ry="0.7" fill="rgba(255,255,210,0.95)" />
              <ellipse cx="-5.2" cy="0.8" rx="1.1" ry="0.7" fill="rgba(255,255,210,0.95)" />
              {/* Headlight glow */}
              <ellipse cx=" 5.2" cy="0.8" rx="2.8" ry="2" fill="rgba(255,255,180,0.25)" />
              {/* Wheels */}
              <circle cx="-3.2" cy="3.6" r="1.9" fill="#111" />
              <circle cx="-3.2" cy="3.6" r="0.7" fill="#555" />
              <circle cx=" 3.2" cy="3.6" r="1.9" fill="#111" />
              <circle cx=" 3.2" cy="3.6" r="0.7" fill="#555" />
              {/* Tail lights */}
              <rect x="-5.5" y="-1.8" width="1.6" height="2.8" rx="0.4" fill="#e53e3e" opacity="0.85" />
              <rect x=" 3.9" y="-1.8" width="1.6" height="2.8" rx="0.4" fill="#e53e3e" opacity="0.85" />

              <animateMotion
                dur="2.6s"
                begin="0s"
                fill="freeze"
                repeatCount="1"
                path={CAR_PATH}
                rotate="auto"
                calcMode="linear"
              />
              <animate
                attributeName="opacity"
                values="0;1;1;1;0"
                keyTimes="0;0.06;0.68;0.88;1"
                dur="2.6s"
                begin="0s"
                fill="freeze"
              />
            </g>
          )}

          {/* ══════════════════════════════════════════
              6. SPEED LINES when car exits top
              ══════════════════════════════════════════ */}
          {animKey > 0 && (
            <g key={`sl-${animKey}`}>
              {[
                { x1:80, y1:24, x2:96, y2:21 },
                { x1:80, y1:33, x2:98, y2:33 },
                { x1:80, y1:42, x2:94, y2:45 },
              ].map((l, i) => (
                <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                  stroke="#d4a017"
                  strokeWidth={1.4 - i * 0.2}
                  strokeLinecap="round"
                >
                  <animate
                    attributeName="opacity"
                    values={`0;0;0;0.${9 - i * 2};0`}
                    keyTimes={`0;0;0.${68 + i};0.${84 + i};1`}
                    dur="2.6s"
                    fill="freeze"
                  />
                </line>
              ))}
            </g>
          )}
        </svg>

        {/* Brand text — React-rendered, NOT from image */}
        {showText && (
          <div className="logo-text-block">
            <span className="logo-text-main">The Unexplored</span>
            <span className="logo-text-sub">Routes</span>
          </div>
        )}
      </div>
    </Link>
  );
}
